// File: clientapp/src/pages/ChangePassword.js
import React, { useState, useEffect } from 'react';
import {
    Container,
    Typography,
    Paper,
    Box,
    TextField,
    Button,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    IconButton
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../contexts/AuthContext';
import { useContext } from 'react';

const passwordCriteria = [
    {
        label: 'At least 6 characters',
        test: (password) => password.length >= 6
    },
    {
        label: 'At least one uppercase letter',
        test: (password) => /[A-Z]/.test(password)
    },
    {
        label: 'At least one lowercase letter',
        test: (password) => /[a-z]/.test(password)
    },
    {
        label: 'At least one digit',
        test: (password) => /[0-9]/.test(password)
    }
];

export default function ChangePassword() {
    const navigate = useNavigate();

    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmNewPassword, setConfirmNewPassword] = useState('');
    const [showCurrent, setShowCurrent] = useState(false);
    const [showNew, setShowNew] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [errors, setErrors] = useState([]);
    const [saveMessage, setSaveMessage] = useState('');
    const [passwordValidations, setPasswordValidations] = useState({
        minLength: false,
        hasUpper: false,
        hasLower: false,
        hasDigit: false,
        matches: false
    });
    const { login } = useContext(AuthContext);

    // Update validations whenever newPassword or confirmNewPassword changes.
    useEffect(() => {
        const minLength = newPassword.length >= 6;
        const hasUpper = /[A-Z]/.test(newPassword);
        const hasLower = /[a-z]/.test(newPassword);
        const hasDigit = /[0-9]/.test(newPassword);
        const matches = newPassword === confirmNewPassword && newPassword !== '';

        setPasswordValidations({
            minLength,
            hasUpper,
            hasLower,
            hasDigit,
            matches
        });
    }, [newPassword, confirmNewPassword]);

    const allValid = Object.values(passwordValidations).every(Boolean);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaveMessage('');
        setErrors([]);
        if (!allValid) {
            setErrors(["Please ensure all password requirements are met."]);
            return;
        }

        try {
            const response = await fetch('/api/account/changePassword', {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    CurrentPassword: currentPassword,
                    NewPassword: newPassword
                })
            });
            if (response.ok) {
                const result = await response.json();
                setSaveMessage(result.message || "Password changed successfully.");

                
                await login(); // Refresh user context


                // Redirect after a short delay
                setTimeout(() => navigate('/'), 1500);
            } else {
                const errorResult = await response.json();
                const errMsg = errorResult.errors
                    ? errorResult.errors.map(e => e.description).join(' ')
                    : errorResult.message;
                setErrors([errMsg]);
            }
        } catch (err) {
            console.error(err);
            setErrors(["An error occurred while changing the password."]);
        }
    };

    return (
        <Container maxWidth="sm" style={{ marginTop: 40 }}>
            <Paper style={{ padding: 24 }}>
                <Typography variant="h5" align="center" gutterBottom>
                    Change Password
                </Typography>
                <Box component="form" onSubmit={handleSubmit}>
                    <TextField
                        label="Current Password"
                        type={showCurrent ? 'text' : 'password'}
                        fullWidth
                        margin="normal"
                        value={currentPassword}
                        onChange={(e) => setCurrentPassword(e.target.value)}
                        required
                        InputProps={{
                            endAdornment: (
                                <IconButton onClick={() => setShowCurrent(!showCurrent)}>
                                    {showCurrent ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                </IconButton>
                            )
                        }}
                    />
                    <TextField
                        label="New Password"
                        type={showNew ? 'text' : 'password'}
                        fullWidth
                        margin="normal"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        required
                        InputProps={{
                            endAdornment: (
                                <IconButton onClick={() => setShowNew(!showNew)}>
                                    {showNew ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                </IconButton>
                            )
                        }}
                    />
                    <TextField
                        label="Confirm New Password"
                        type={showConfirm ? 'text' : 'password'}
                        fullWidth
                        margin="normal"
                        value={confirmNewPassword}
                        onChange={(e) => setConfirmNewPassword(e.target.value)}
                        required
                        InputProps={{
                            endAdornment: (
                                <IconButton onClick={() => setShowConfirm(!showConfirm)}>
                                    {showConfirm ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                </IconButton>
                            )
                        }}
                    />

                    <Box mt={2}>
                        <Typography variant="subtitle1">Password Requirements:</Typography>
                        <List>
                            {passwordCriteria.map((criterion, index) => {
                                const met = criterion.test(newPassword);
                                return (
                                    <ListItem key={index}>
                                        <ListItemIcon>
                                            {met ? (
                                                <CheckCircleIcon style={{ color: 'green' }} />
                                            ) : (
                                                <CancelIcon style={{ color: 'red' }} />
                                            )}
                                        </ListItemIcon>
                                        <ListItemText primary={criterion.label} />
                                    </ListItem>
                                );
                            })}
                        </List>
                    </Box>

                    {errors.length > 0 && (
                        <Box mt={2}>
                            {errors.map((err, index) => (
                                <Typography key={index} variant="body2" color="error">
                                    {err}
                                </Typography>
                            ))}
                        </Box>
                    )}

                    {saveMessage && (
                        <Box mt={2}>
                            <Typography variant="body2" color="primary">
                                {saveMessage}
                            </Typography>
                        </Box>
                    )}

                    <Box mt={3} display="flex" justifyContent="center">
                        <Button variant="contained" color="primary" type="submit" disabled={!allValid}>
                            Save Password
                        </Button>
                    </Box>
                </Box>
            </Paper>
        </Container>
    );
}
